import React, { useMemo, useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { SectionCard, EmptyState, Money } from "@/components/common/ui";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { seriesColor } from "@/lib/chart";
import {
  Building2, Landmark, Cpu, ReceiptText, Search, Layers, Store, ListFilter,
} from "lucide-react";

const fmtCount = (n) => new Intl.NumberFormat("en-IN").format(n || 0);
const pctOf = (v, t) => (t ? Math.round((v / t) * 100) : 0);
const DASH = "\u2014";

// The three corporate entities the business tracks. "Brand" == Sumadhura.
const ENTITY_CFG = [
  {
    key: "Sumadhura", label: "Brand", sub: "Sumadhura \u00b7 brand-level spend",
    icon: Building2, accent: "hsl(var(--chart-cat-2))",
    categoryField: "by_vertical", categoryTitle: "Spend by vertical", categoryUnit: "Verticals",
  },
  {
    key: "Signet", label: "Signet", sub: "Office expenses",
    icon: Landmark, accent: "hsl(var(--chart-cat-5))",
    categoryField: "by_activity", categoryTitle: "Spend by category", categoryUnit: "Cost heads",
  },
  {
    key: "Tech", label: "Tech", sub: "Technology services",
    icon: Cpu, accent: "hsl(var(--chart-cat-1))",
    categoryField: "by_activity", categoryTitle: "Spend by service", categoryUnit: "Services",
  },
];

/* ---------------- horizontal bar list (tidy, always readable) ---------------- */
function BarList({ items = [], money, max = 8, colorOffset = 0, testId }) {
  const top = items.slice(0, max);
  const maxV = top.length ? Math.max(...top.map((r) => r.spent || 0)) : 0;
  const grand = items.reduce((s, r) => s + (r.spent || 0), 0);
  if (!top.length) {
    return <p className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">No spend recorded.</p>;
  }
  return (
    <div className="space-y-1.5" data-testid={testId}>
      {top.map((r, i) => {
        const barPct = maxV ? Math.max(3, Math.round(((r.spent || 0) / maxV) * 100)) : 0;
        const color = seriesColor((i + colorOffset) % 8);
        return (
          <div key={r.name} className="relative flex items-center gap-3 overflow-hidden rounded-md border bg-card px-3 py-2"
            data-testid={`${testId}-row`} title={r.name}>
            <span aria-hidden className="absolute inset-y-0 left-0" style={{ width: `${barPct}%`, background: `${color}1f` }} />
            <span className="relative z-10 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: color }} />
            <span className="relative z-10 min-w-0 flex-1 truncate text-sm font-medium">{r.name}</span>
            <span className="relative z-10 w-9 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{pctOf(r.spent, grand)}%</span>
            {r.count != null && <span className="relative z-10 hidden w-14 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground sm:inline">{r.count} lines</span>}
            <span className="num relative z-10 w-24 shrink-0 text-right text-sm font-semibold tabular-nums">{money(r.spent)}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- KPI chip ---------------- */
function Chip({ label, value, accent, testId }) {
  return (
    <div className="rounded-lg border bg-[hsl(var(--surface-2))] p-3" data-testid={testId}>
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="num mt-1 truncate font-heading text-lg font-semibold tabular-nums" style={accent ? { color: accent } : undefined}>{value}</p>
    </div>
  );
}

function SubHead({ icon: Icon, children, accent }) {
  return (
    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3.5 w-3.5" style={accent ? { color: accent } : undefined} /> {children}
    </p>
  );
}

/* ---------------- one entity's full, consistent breakdown ---------------- */
function EntityDetail({ cfg, data, money }) {
  const { key, label, sub, icon: Icon, accent, categoryField, categoryTitle, categoryUnit } = cfg;
  const [q, setQ] = useState("");

  const cats = useMemo(() => (data?.[categoryField] || []).map((c) => ({ ...c, name: c.label || c.name })), [data, categoryField]);
  const vendors = data?.by_vendor || [];

  const ledger = useMemo(() => {
    const rows = data?.activities || [];
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r.activity, r.vertical, r.platform, r.vendor].some((v) => String(v || "").toLowerCase().includes(term)));
  }, [data, q]);

  if (!data || data.total <= 0) {
    return (
      <SectionCard testId={`corp-detail-${key}`} className="border-l-[3px]" style={{ borderLeftColor: accent }}
        title={<EntityTitle icon={Icon} accent={accent} label={label} sub={sub} />}>
        <div className="p-6"><EmptyState title={`No ${label} spend`} message="No corporate-tagged spend for this entity in the selected period." /></div>
      </SectionCard>
    );
  }

  return (
    <SectionCard testId={`corp-detail-${key}`} className="border-l-[3px]" style={{ borderLeftColor: accent }}
      title={<EntityTitle icon={Icon} accent={accent} label={label} sub={sub} />}
      action={<div className="text-right"><p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Total</p><Money value={data.total} className="font-heading text-lg font-semibold" testId={`corp-detail-total-${key}`} /></div>}>
      <div className="space-y-5 p-4">
        {/* KPI chips */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Chip label="Total spend" value={money(data.total)} accent={accent} testId={`corp-chip-total-${key}`} />
          <Chip label="Line items" value={fmtCount(data.count)} testId={`corp-chip-count-${key}`} />
          <Chip label={categoryUnit} value={fmtCount(cats.length)} testId={`corp-chip-cats-${key}`} />
          <Chip label="Top vendor" value={<span className="text-sm">{data.top_vendor || DASH}</span>} testId={`corp-chip-vendor-${key}`} />
        </div>

        {/* category + vendors side by side */}
        <div className="grid gap-5 lg:grid-cols-2">
          <div>
            <SubHead icon={Layers} accent={accent}>{categoryTitle}</SubHead>
            <BarList items={cats} money={money} max={8} colorOffset={1} testId={`corp-cat-${key}`} />
          </div>
          <div>
            <SubHead icon={Store} accent={accent}>Top vendors</SubHead>
            <BarList items={vendors} money={money} max={8} colorOffset={3} testId={`corp-vendor-${key}`} />
          </div>
        </div>

        {/* searchable activity ledger */}
        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <SubHead icon={ReceiptText} accent={accent}>Activity ledger</SubHead>
            <div className="relative w-full sm:w-64">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search activity, vendor, vertical…"
                className="h-8 pl-8 text-xs" data-testid={`corp-ledger-search-${key}`} />
            </div>
          </div>
          <div className="overflow-hidden rounded-lg border">
            <div className="max-h-[440px] overflow-y-auto overscroll-contain" data-testid={`corp-ledger-scroll-${key}`}>
              <table className="w-full border-collapse" data-testid={`corp-ledger-${key}`}>
                <thead className="sticky top-0 z-10">
                  <tr>
                    {["Activity", "Vertical", "Platform", "Vendor", "Lines", "Amount"].map((h, i) => (
                      <th key={h} className={`border-b bg-[hsl(var(--surface-2))] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground ${["Amount", "Lines"].includes(h) ? "text-right" : "text-left"} ${i === 0 ? "th-sticky-left" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ledger.length === 0 ? (
                    <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-muted-foreground">No line items match &ldquo;{q}&rdquo;.</td></tr>
                  ) : ledger.map((r, i) => (
                    <tr key={i} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`}>
                      <td className="td-sticky-left bg-card px-3 py-2 text-sm font-medium">{r.activity}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.vertical}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.platform}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.vendor}</td>
                      <td className="px-3 py-2 num text-right text-xs tabular-nums text-muted-foreground">{fmtCount(r.count)}</td>
                      <td className="px-3 py-2 num text-right text-sm font-semibold tabular-nums">{money(r.spent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground" data-testid={`corp-ledger-count-${key}`}>
            Showing {fmtCount(ledger.length)} of {fmtCount((data.activities || []).length)} activities · {fmtCount(data.count)} line items
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

function EntityTitle({ icon: Icon, accent, label, sub }) {
  return (
    <span className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}26`, color: accent }}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: accent }}>Corporate entity</span>
        <span className="text-base font-semibold">{label} <span className="text-xs font-normal text-muted-foreground">· {sub}</span></span>
      </span>
    </span>
  );
}

/**
 * The full "Corporate Spends" view shown when the dashboard is toggled to
 * corporate scope: a top-level split of the three entities (Brand / Signet /
 * Tech) followed by a detailed, consistent breakdown for each — spend by
 * category, top vendors, month-over-month trend and a searchable ledger.
 * Corporate spend is never blended with project spend.
 */
export function CorporateOverview({ period = "FY", overview, loading }) {
  const { money } = useCurrency();
  const [entities, setEntities] = useState({});
  const [entLoading, setEntLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setEntLoading(true);
    Promise.all(
      ENTITY_CFG.map((c) => api.corporateEntity(c.key, period).then((d) => [c.key, d]).catch(() => [c.key, null]))
    )
      .then((pairs) => { if (on) setEntities(Object.fromEntries(pairs)); })
      .finally(() => on && setEntLoading(false));
    return () => { on = false; };
  }, [period]);

  const rows = ENTITY_CFG.map((c) => {
    const d = entities[c.key] || {};
    return { ...c, total: d.total || 0, count: d.count || 0, topVendor: d.top_vendor || null };
  });
  const grand = rows.reduce((s, r) => s + r.total, 0);
  const totalCount = rows.reduce((s, r) => s + r.count, 0);
  const busy = (loading && !overview) || (entLoading && grand === 0);

  return (
    <div className="space-y-4" data-testid="corporate-spends-view">
      {/* ---------- split overview ---------- */}
      <SectionCard
        title="Corporate Spends"
        description="Company-level spend split across Brand (Sumadhura), Signet and Tech — corporate spends only, never blended with project spends."
        testId="card-corporate-overview"
        action={grand > 0 ? (
          <div className="text-right" data-testid="corp-overview-total">
            <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Total corporate spend</p>
            <Money value={grand} className="font-heading text-lg font-semibold" />
          </div>
        ) : null}
      >
        {busy ? (
          <div className="space-y-4 p-4">
            <Skeleton className="h-3 w-full rounded-full" />
            <div className="grid gap-3 md:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
          </div>
        ) : grand > 0 ? (
          <div className="space-y-5 p-4">
            <div>
              <div className="mb-1.5 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="font-semibold uppercase tracking-wide">Spend split</span>
                <span data-testid="corp-overview-lineitems">{fmtCount(totalCount)} line items</span>
              </div>
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-[hsl(var(--surface-3))]" data-testid="corp-split-bar">
                {rows.map((r) => (r.total > 0 ? (
                  <div key={r.key} title={`${r.label} \u00b7 ${money(r.total)} (${pctOf(r.total, grand)}%)`}
                    style={{ width: `${(r.total / grand) * 100}%`, background: r.accent }} />
                ) : null))}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                {rows.map((r) => (
                  <span key={r.key} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: r.accent }} />
                    {r.label} <span className="tabular-nums text-foreground">{pctOf(r.total, grand)}%</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3" data-testid="corp-entity-cards">
              {rows.map((r) => {
                const Icon = r.icon;
                return (
                  <div key={r.key} className="rounded-xl border bg-card p-4 transition-colors duration-150 hover:border-[hsl(var(--primary)/0.35)]"
                    style={{ borderTop: `3px solid ${r.accent}` }} data-testid={`corp-entity-card-${r.key}`}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="flex items-center gap-2.5">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${r.accent}26`, color: r.accent }}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="flex flex-col leading-tight">
                          <span className="text-sm font-semibold">{r.label}</span>
                          <span className="text-[11px] text-muted-foreground">{r.sub}</span>
                        </span>
                      </span>
                      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums" style={{ background: `${r.accent}1a`, color: r.accent }} data-testid={`corp-entity-pct-${r.key}`}>
                        {pctOf(r.total, grand)}%
                      </span>
                    </div>
                    <p className="num mt-3 font-heading text-2xl font-semibold tabular-nums" data-testid={`corp-entity-total-${r.key}`}>{money(r.total)}</p>
                    <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                      <span data-testid={`corp-entity-count-${r.key}`}>{fmtCount(r.count)} line items</span>
                      {r.topVendor && (
                        <span className="flex items-center gap-1 truncate" title={r.topVendor}>
                          <ReceiptText className="h-3 w-3 shrink-0" /> {r.topVendor}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="p-6"><EmptyState title="No corporate spend" message="No corporate-tagged spend for this period." /></div>
        )}
      </SectionCard>

      {/* ---------- detailed per-entity breakdowns (stacked) ---------- */}
      {busy ? (
        <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-72 w-full rounded-xl" />)}</div>
      ) : grand > 0 ? (
        <div className="space-y-4" data-testid="corp-entity-details">
          <p className="flex items-center gap-1.5 px-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" /> Detailed mapping by entity
          </p>
          {ENTITY_CFG.map((cfg) => (
            <EntityDetail key={cfg.key} cfg={cfg} data={entities[cfg.key]} money={money} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
